import { AutocompleteWithLabel } from '@adotel/components'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumb from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { CHANNELS_QUERY } from '../../graphql/channel'
import {
  PROMOTION_QUERY,
  SAVE_PROMOTION_MUTATION,
  Promotion,
  PromotionInput
} from '../../graphql/promotions'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import {
  isPercentPrecisionExact,
  percentToBasisPoints,
  toScopeOptions
} from '../../helpers/promotions'
import { useRegisterSupportScreen } from '../../helpers/supportScreenContext'
import { getTranslate, lang } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { PromotionDistribution } from '../../types/cart'
import { Channel } from '../../types/channel'
import { CurrencyList } from '../../types/currency'
import PromotionBuilderBackButton from './PromotionBuilderBackButton'
import {
  emptyForm,
  toApiRanges,
  toForm,
  toOptionalInt,
  toOptionalIntString
} from './form'
import LimitsSection from './sections/LimitsSection'
import ScheduleSection from './sections/ScheduleSection'
import ScopeSection from './sections/ScopeSection'
import PromotionStatsPanel from './stats/PromotionStatsPanel'
import useStyles from './styles'
import { BuilderForm, DateRangeValue, ScopeOption } from './types'

type ChannelOption = { label: string; value: string }

// Same shape as ChannelOption, kept as its own name because the two pickers differ
// in arity: channels are a multi-select, provider is exactly one (the schema's
// `provider_code` is REQUIRED and singular — the party supplying the rate).
type ProviderOption = { label: string; value: string }

/**
 * Only the fields this screen reads. The full shapes are large and their extra fields are
 * irrelevant to a scope picker, which needs a code to store, a name to show, and the display
 * code that makes the option readable — the stored code is encrypted by the BFF and is not
 * something an operator can recognise.
 */
type AccommodationRow = {
  basicDetails?: {
    name?: string
    propertyCode?: string
    displayPropertyCode?: string
  } | null
}
type RoomTypeRow = {
  roomTypeCode?: string | null
  displayRoomTypeCode?: string | null
  name?: string | null
}
type RatePlanRow = {
  ratePlanCode?: string | null
  displayRatePlanCode?: string | null
  name?: string | null
}

type ProviderRow = {
  providerCode?: string | null
  displayProviderCode?: string | null
  name?: string | null
}

// Priority is `integer NOT NULL DEFAULT 0` in the schema with NO upper bound, and higher
// wins stacking (internal/promo/stacking.go sorts descending). So this range is a UI
// choice, not a constraint the backend imposes — 0-10 covers the stacking tiers an
// operator realistically needs while keeping the control a picker rather than free text.
//
// An existing promotion may carry a value outside it, positive or negative (nothing stops
// the API accepting 50, and -5 rows exist). priorityOptions() folds the current value in
// rather than dropping it — a picker that silently rewrites a loaded value to the nearest
// option is data loss on save, and it would look like the operator did it.
//
// An earlier version excluded NEGATIVES here, reasoning that the submit validator was
// /^\d+$/ so offering one would let the operator pick a value the form then refuses.
// That was the wrong lever and it did not work: priorityOptions() only decides what is
// SELECTABLE, while form.priority keeps the loaded "-5" until someone changes it — so
// the save stayed blocked and the operator could not rename such a promotion at all.
// The validator now accepts negatives instead. This form edits existing data; it is not
// the authority on what the backend considers valid.

// Shared by the picker's derived value AND the submit payload. It lived in only the
// first of those, so a promotion loaded with "A, A, B" showed two chips and saved three
// codes — the displayed value and the stored value disagreed for any operator who edited
// something else and saved without touching the channel field.
const dedupeChannelCodes = (codes: string): string[] =>
  Array.from(
    new Set(
      codes
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean)
    )
  )

const PRIORITY_MAX = 10

const priorityOptions = (current: string): number[] => {
  const base = Array.from({ length: PRIORITY_MAX + 1 }, (_, index) => index)
  const parsed = Number(current)
  if (current !== '' && Number.isInteger(parsed) && !base.includes(parsed))
    return [...base, parsed].sort((a, b) => a - b)
  return base
}

/**
 * The fields this form does not edit, carried through from what was loaded.
 *
 * WHITELISTED, not spread. The read type carries `id`, `status`, `canWrite`, `updatedAt`
 * and Apollo's `__typename`, none of which exist on PromotionInput — spreading the loaded
 * promotion wholesale would have the server reject the mutation on unknown fields, turning
 * a data-loss bug into an every-save-fails bug.
 *
 * `stripTypename` handles the nested objects for the same reason: Apollo attaches
 * __typename at every level, so `scope` and `schedule` cannot be passed through as-is
 * either.
 */
const preservedFromLoaded = (
  promotion?: Promotion
): Partial<PromotionInput> => {
  if (!promotion) return {}
  const preserved: Partial<PromotionInput> = {}
  // `caps` and `consumableVia` are deliberately NOT preserved any more. They used to be,
  // because the form rendered no control for either and a save would otherwise erase them.
  // The form now owns both, loads the saved values into them and sends them explicitly —
  // and preserving them here as well would make the loaded value win over an edit, so
  // clearing a budget cap or narrowing a booking surface would appear to work and silently
  // save the old value. That is the same trap `code` was pulled out of, below.
  // `code` is deliberately NOT preserved here any more. It used to be, because the form
  // rendered no field for it and a save would otherwise drop the coupon while keeping
  // codeMode — a promotion requiring a code nobody could enter. The form now owns the
  // field, loads the existing value into it, and sends it explicitly, so preserving it
  // here as well would make the loaded value win over an edit: clearing or changing a
  // coupon would appear to work and silently save the old one.
  if (promotion.decisionTableRef)
    preserved.decisionTableRef = promotion.decisionTableRef
  return preserved
}

const PromotionBuilder: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { id } = useParams<{ id?: string }>()
  const [form, setForm] = useState<BuilderForm>(emptyForm)
  const [formError, setFormError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const {
    data,
    loading: detailLoading,
    error: detailError,
    refetch
  } = useQuery<{
    promotion: Promotion
  }>(PROMOTION_QUERY, {
    variables: { id },
    skip: !id,
    fetchPolicy: id ? 'network-only' : 'cache-first'
  })
  const loadedSupportPromotion =
    id && !detailLoading && !detailError ? data?.promotion : undefined
  useRegisterSupportScreen({
    affectedEntities: loadedSupportPromotion?.providerCode
      ? [
          {
            type: 'provider',
            code: loadedSupportPromotion.providerCode,
            label:
              loadedSupportPromotion.providerName ||
              loadedSupportPromotion.providerCode
          }
        ]
      : [],
    relatedPromotions: loadedSupportPromotion
      ? [{ id: loadedSupportPromotion.id, label: loadedSupportPromotion.name }]
      : []
  })
  // Which action is in flight, so the completion handler can name it. Apollo's
  // onCompleted receives the RESPONSE, not the variables, and a "Draft saved" toast
  // shown after a Publish would be worse than the silence it replaces. A ref rather
  // than state: nothing renders from it, so setting it must not trigger a re-render
  // mid-submit.
  //
  // Declared ABOVE the mutation that closes over it. It previously sat beside submit(),
  // several hundred lines below the onCompleted handler that reads it. That is harmless
  // at runtime -- onCompleted fires long after the component body has run -- but it is a
  // no-use-before-define error, and react-scripts escalates eslint errors to a FAILED
  // production build. The build emitted no index.html, the image guard refused to ship
  // an empty bundle, and the release aborted. This repo has no CI (its last pipeline ran
  // in 2021), so the release itself was the first thing to execute a production build.
  const publishIntent = React.useRef(false)

  const [savePromotion, { loading: saving }] = useMutation<{
    savePromotion: Promotion
  }>(SAVE_PROMOTION_MUTATION, {
    update: (cache, result) => {
      const promotion = result.data?.savePromotion
      if (promotion) {
        cache.writeQuery({
          query: PROMOTION_QUERY,
          variables: { id: promotion.id },
          data: { promotion }
        })
      }
    },
    onCompleted: () => {
      // Success was the QUIETEST outcome on this screen: failure toasted, success
      // navigated silently — so a completed save looked identical to a Cancel, since
      // you land on the list either way. Publish especially: the operator had no
      // confirmation that a promotion is now live and discounting real bookings.
      //
      // react-toastify renders into an aria-live container, so this closes the
      // WCAG 4.1.3 status-message gap and the usability gap in one move.
      toast.success(
        getTranslate(
          publishIntent.current ? 'Promotion published.' : 'Draft saved.'
        )
      )
      history.push(`/${lang}/promotions`)
    },
    /*
     * Say WHY the save was refused.
     *
     * This discarded the error and toasted a fixed sentence, so every refusal looked the
     * same: a cap that cannot be lowered below its redemptions, a code already in use, a
     * date range the service rejects. An operator reported being unable to clear a
     * max-uses cap on a promotion that had never been redeemed, and neither they nor
     * anyone reading the report could tell what the service had actually objected to —
     * the one sentence that would have explained it was thrown away here.
     *
     * The generic line stays as the fallback for an error carrying no message (a network
     * failure has none worth showing), which is the only case it was ever right for.
     */
    onError: (err) =>
      toast.error(err.message || getTranslate('Unable to save promotion.'))
  })

  // Channels back the Channel-codes picker. Fetched on mount, NOT deferred to first
  // interaction — an earlier version of this comment claimed the opposite, and the effect
  // below has always fired unconditionally. It has to: an edit-mode promotion arrives
  // with codes that need resolving to names before the operator opens anything.
  // useLazyQuery is used only to control WHEN relative to the other effects.
  const [
    getChannels,
    { data: channelsData, loading: channelsLoading, error: channelsError }
  ] = useLazyQuery<{ channels: Channel[] }>(CHANNELS_QUERY)

  // Providers back the Provider-code picker. Same fetch-on-mount reasoning as
  // channels: an edit-mode promotion arrives holding a code that has to resolve to a
  // name before the operator opens anything.
  const [
    getProviders,
    { data: providersData, loading: providersLoading, error: providersError }
  ] = useLazyQuery<{ providers: ProviderRow[] }>(PROVIDERS_QUERY)

  // Properties back the scope picker, narrowed to the promotion's own provider — a
  // promotion can only discount supply that provider actually holds, so offering the whole
  // estate would invite a scope the evaluator will never match.
  const [
    getAccommodations,
    { data: accommodationsData, loading: accommodationsLoading }
  ] = useLazyQuery<{ accommodations: AccommodationRow[] }>(ACCOMMODATIONS_QUERY)

  // Room types and rate plans are per-PROPERTY: both queries take a single required
  // `propertyCode`. A promotion may scope several, so the list is fetched for the first
  // one and the fields accept free text for the rest — see ScopeSection. Fetching per
  // property and merging was the alternative and is worse: the codes are not unique across
  // properties, so the merged list would offer the same code twice with different meanings.
  const [getRoomTypes, { data: roomTypesData, loading: roomTypesLoading }] =
    useLazyQuery<{ roomTypes: RoomTypeRow[] }>(ROOMTYPES_QUERY)
  const [getRatePlans, { data: ratePlansData, loading: ratePlansLoading }] =
    useLazyQuery<{ ratePlans: RatePlanRow[] }>(RATEPLANS_QUERY)

  useEffect(() => {
    getChannels()
  }, [getChannels])

  useEffect(() => {
    getProviders()
  }, [getProviders])

  useEffect(() => {
    if (form.providerCode)
      getAccommodations({ variables: { providerCode: form.providerCode } })
  }, [getAccommodations, form.providerCode])

  const firstProperty = form.propertyCodes[0]

  useEffect(() => {
    if (!firstProperty) return
    const variables = {
      propertyCode: firstProperty,
      providerCode: form.providerCode || undefined
    }
    getRoomTypes({
      variables: {
        ...variables,
        showDeletedRoomTypes: false,
        showDeactivedRoomTypes: false
      }
    })
    getRatePlans({
      variables: {
        ...variables,
        showDeletedRatePlans: false,
        showDeactivedRatePlans: false
      }
    })
  }, [getRoomTypes, getRatePlans, firstProperty, form.providerCode])

  useEffect(() => {
    if (data?.promotion) setForm(toForm(data.promotion))
  }, [data])

  const update = (key: keyof BuilderForm, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }))

  const updateList = (
    key: 'propertyCodes' | 'roomTypeCodes' | 'ratePlanCodes' | 'consumableVia',
    value: string[]
  ) =>
    setForm((current) => {
      // Clearing the property also clears what was picked FROM a property. The codes are
      // property-scoped, so leaving them behind would keep a room type that no longer has a
      // property to belong to — and the operator cannot see the mismatch, because the list
      // it came from is gone with the property.
      if (key === 'propertyCodes' && value.length === 0)
        return {
          ...current,
          propertyCodes: [],
          roomTypeCodes: [],
          ratePlanCodes: []
        }
      return { ...current, [key]: value }
    })

  const updateRanges = (
    key: 'stayRanges' | 'bookingRanges' | 'blackoutRanges',
    ranges: DateRangeValue[]
  ) => setForm((current) => ({ ...current, [key]: ranges }))

  const propertyOptions: ScopeOption[] = useMemo(
    () =>
      toScopeOptions(
        (accommodationsData?.accommodations || []).map((row) => ({
          code: row.basicDetails?.propertyCode,
          displayCode: row.basicDetails?.displayPropertyCode,
          name: row.basicDetails?.name
        }))
      ),
    [accommodationsData]
  )

  const roomTypeOptions: ScopeOption[] = useMemo(
    () =>
      toScopeOptions(
        (roomTypesData?.roomTypes || []).map((row) => ({
          code: row.roomTypeCode,
          displayCode: row.displayRoomTypeCode,
          name: row.name
        }))
      ),
    [roomTypesData]
  )

  const ratePlanOptions: ScopeOption[] = useMemo(
    () =>
      toScopeOptions(
        (ratePlansData?.ratePlans || []).map((row) => ({
          code: row.ratePlanCode,
          displayCode: row.displayRatePlanCode,
          name: row.name
        }))
      ),
    [ratePlansData]
  )

  const channelOptions: ChannelOption[] = useMemo(
    () =>
      (channelsData?.channels || [])
        // A channel with no code cannot scope a promotion. Offering one produced a chip
        // that vanished on the very next render — selected, joined as an empty segment,
        // then dropped by the filter below — so the operator saw their click do nothing,
        // repeatedly. Not selectable is honest; selectable-then-discarded is not.
        .filter((channel) => channel.basicDetails.channelCode)
        .map((channel) => ({
          // Operators recognise a channel by name; the promotion is scoped by its code.
          // Showing only the code would make this a picker you need a lookup table for.
          label: channel.basicDetails.displayChannelCode
            ? `${channel.basicDetails.name} (${channel.basicDetails.displayChannelCode})`
            : channel.basicDetails.name,
          value: channel.basicDetails.channelCode as string
        })),
    [channelsData]
  )

  // The form still holds a comma-joined string, so the picker's value is derived rather
  // than stored. A code that no longer resolves to a live channel — renamed, retired,
  // or simply not yet loaded — is kept and shown as its bare code instead of vanishing
  // from the field on open, which would delete it from the promotion on the next save.
  const providerOptions: ProviderOption[] = useMemo(
    () =>
      (providersData?.providers || [])
        // A provider with no code cannot own a promotion — same reason codeless
        // channels are filtered: selectable-then-discarded is worse than absent.
        .filter((provider) => provider.providerCode)
        .map((provider) => ({
          label: provider.displayProviderCode
            ? `${provider.name || provider.providerCode} (${
                provider.displayProviderCode
              })`
            : provider.name || (provider.providerCode as string),
          value: provider.providerCode as string
        })),
    [providersData]
  )

  // An existing promotion can hold a provider code that no longer resolves — the
  // provider was retired, or this operator cannot see it. Falling back to a
  // code-as-label option keeps the value visible and saveable instead of blanking
  // the field, which would silently rewrite the promotion's owner on the next save.
  // Held as an ARRAY of at most one. AutocompleteWithLabel is typed
  // `AutocompleteProps<…, Multiple = true>` — the design system has no searchable
  // single-select (FreeSoloAutocompleteWithLabel is also multi; SelectWithLabel is a
  // TextField and not searchable). Rather than drop to a raw MUI Autocomplete and
  // diverge from the design system, singularity is enforced in onChange below.
  const selectedProvider: ProviderOption[] = useMemo(() => {
    const code = form.providerCode.trim()
    if (!code) return []
    // The fallback fires whenever the promotion's provider is not in the caller's own
    // provider list — an admin editing a promotion belonging to another provider, most
    // obviously. Labelling it with the raw code was unreadable there: provider codes travel
    // as opaque `prv_…` tokens, so the chip rendered ~90 characters of ciphertext.
    //
    // `providerName` is server-resolved and survives that, so prefer it. The VALUE stays the
    // code either way — it is what the mutation sends, and changing it would break the save.
    return [
      providerOptions.find((option) => option.value === code) || {
        label: data?.promotion?.providerName || code,
        value: code
      }
    ]
  }, [form.providerCode, providerOptions, data?.promotion?.providerName])

  const selectedChannels: ChannelOption[] = useMemo(() => {
    const codes = dedupeChannelCodes(form.channelCodes)
    return codes.map(
      (code) =>
        channelOptions.find((option) => option.value === code) || {
          label: code,
          value: code
        }
    )
  }, [form.channelCodes, channelOptions])

  // Both calendars, because TimePeriodForm renders `lang === 'fa' ? jalaaliStartDate :
  // gregorianStartDate`. Supplying only the Gregorian halves made the control render EMPTY
  // for every Persian operator — and re-picking the dates did not help, since onChange
  // stores only the Gregorian strings and this memo rebuilt without the Jalaali ones. So a
  // Farsi operator could neither read the promotion's validity period nor confirm their
  // own input.
  //
  // DERIVED here rather than captured from onChange: a loaded promotion never fires
  // onChange, and that is exactly the case reported.

  const updateDistribution = (distribution: PromotionDistribution) =>
    setForm((current) => ({
      ...current,
      distribution,
      advertisementOptIn:
        distribution === 'CodeEntry' && current.advertisementOptIn
    }))

  // A promotion that mints codes must be redeemed BY code, so choosing a code mode moves
  // distribution to CodeEntry rather than leaving an invalid pair on screen.
  //
  // The pairing is not cosmetic. Loyalty reserves a voucher only when a code was SUPPLIED,
  // and an auto-applied promotion supplies none — so BulkVoucher + AutoApplied handed every
  // cart a discount while the voucher pool stayed untouched, and the voucher count that
  // bounds an operator's exposure gated nothing. SingleShared + AutoApplied is the harmless
  // half: the code is simply never checked.
  //
  // Corrected here as well as disabled in the menu, because the invalid pair is reachable
  // by EDITING an existing AutoApplied promotion — without this, changing its code mode
  // would leave it invalid on screen until the server refused the save.
  const updateCodeMode = (codeMode: string) =>
    setForm((current) => ({
      ...current,
      codeMode,
      code: codeMode === 'SingleShared' ? current.code : '',
      distribution: codeMode === 'None' ? current.distribution : 'CodeEntry'
    }))

  const submit = (event: React.SyntheticEvent, publish = false) => {
    event.preventDefault()
    publishIntent.current = publish
    setFormError('')
    setFieldErrors({})
    const errors: Record<string, string> = {}
    if (
      !form.name.trim() ||
      !form.providerCode.trim() ||
      !form.discountValue.trim() ||
      (form.discountType !== 'Percent' && !form.currency.trim())
    ) {
      // Currency is required only for FixedAmount. A percentage is not denominated in
      // anything — formatDiscount deliberately omits the currency when rendering one — so
      // requiring it forced the operator to pick a value the product then refuses to
      // display, and stored whatever they guessed.
      const requiredKeys: Array<
        'name' | 'providerCode' | 'discountValue' | 'currency'
      > =
        form.discountType === 'Percent'
          ? ['name', 'providerCode', 'discountValue']
          : ['name', 'providerCode', 'discountValue', 'currency']
      requiredKeys.forEach((key) => {
        if (!form[key].trim())
          errors[key] = getTranslate('This field is required.')
      })
      setFieldErrors(errors)
      setFormError(getTranslate('Complete the highlighted fields.'))
      return
    }
    // A percent the API cannot store, caught here so the operator is told WHICH field is
    // wrong. Without it the server rejects the save with a message about basis points — a
    // unit this form deliberately never shows them, so the error would be unactionable.
    if (form.discountType === 'Percent') {
      if (!isPercentPrecisionExact(form.discountValue)) {
        setFieldErrors({
          discountValue: getTranslate('Use at most two decimal places.')
        })
        setFormError(getTranslate('Complete the highlighted fields.'))
        return
      }
      if (percentToBasisPoints(form.discountValue) === null) {
        setFieldErrors({
          discountValue: getTranslate('Enter a percent between 0 and 100.')
        })
        setFormError(getTranslate('Complete the highlighted fields.'))
        return
      }
    }
    // Each list is checked as a whole and reported on the list, not on a row. A row has no
    // stable identity — it is positional — so a per-row error would follow the wrong row
    // the moment one above it is removed.
    const rangeError = (
      [
        ['stayRanges', form.stayRanges],
        ['bookingRanges', form.bookingRanges],
        ['blackoutRanges', form.blackoutRanges]
      ] as Array<[string, DateRangeValue[]]>
    )
      .map(([key, ranges]) => {
        const halfFilled = ranges.some(
          (range) => Boolean(range.start) !== Boolean(range.end)
        )
        if (halfFilled)
          return [key, getTranslate('Enter both start and end dates.')] as const
        const reversed = ranges.some(
          (range) => range.start && range.end && range.start > range.end
        )
        if (reversed)
          return [
            key,
            getTranslate('Start date must be before end date.')
          ] as const
        return null
      })
      .find(Boolean)

    if (rangeError) {
      setFieldErrors({ [rangeError[0]]: rangeError[1] })
      setFormError(getTranslate('Complete the highlighted fields.'))
      return
    }

    // Both bounds or neither ordering. A minimum above a maximum is accepted by neither the
    // API nor the evaluator, and catching it here names the two fields rather than
    // surfacing a generic rejection.
    const apMin = toOptionalInt(form.advancePurchaseMinDays)
    const apMax = toOptionalInt(form.advancePurchaseMaxDays)
    if (apMin !== undefined && apMax !== undefined && apMin > apMax) {
      setFieldErrors({
        advancePurchaseMinDays: getTranslate(
          'The minimum must not be greater than the maximum.'
        ),
        advancePurchaseMaxDays: getTranslate(
          'The maximum must not be less than the minimum.'
        )
      })
      setFormError(getTranslate('Complete the highlighted fields.'))
      return
    }
    // Accepts negatives. The backend stores them — this form is an editor for existing
    // data, not the authority on what is valid — so refusing to SAVE one meant an
    // operator could not rename a promotion that happened to hold priority -5. Narrowing
    // the picker (the previous attempt) never fixed that: it changed what was selectable
    // while form.priority kept the loaded value, so the block survived untouched.
    if (!/^-?\d+$/.test(form.priority)) {
      setFieldErrors({
        priority: getTranslate('Priority must be a whole number.')
      })
      setFormError(getTranslate('Complete the highlighted fields.'))
      return
    }
    const input: PromotionInput = {
      ...(id ? { id, updatedAt: form.updatedAt } : {}),
      name: form.name.trim(),
      providerCode: form.providerCode.trim(),
      discountType: form.discountType,
      // Converted back on the way out. The validation above has already rejected anything
      // percentToBasisPoints would refuse, so the non-null assertion here is safe — but it
      // is written as a fallback rather than a `!` so a future edit that reorders the
      // checks degrades to the old behaviour instead of crashing.
      discountValue:
        form.discountType === 'Percent'
          ? String(
              percentToBasisPoints(form.discountValue) ??
                form.discountValue.trim()
            )
          : form.discountValue.trim(),
      currency: form.currency.trim(),
      codeMode: form.codeMode,
      // Sent only for SingleShared; the other modes have no coupon and an empty
      // string would be persisted as one.
      code: form.codeMode === 'SingleShared' ? form.code.trim() : undefined,
      distribution: form.distribution,
      advertisementOptIn:
        form.distribution === 'CodeEntry' && form.advertisementOptIn,
      stackable: form.stackable,
      priority: Number(form.priority) || 0,
      // EVERY field the form does not edit is carried through from what was loaded.
      //
      // savePromotion does NOT merge. Traced end to end: capsToPB(nil) in the BFF returns
      // an empty Caps rather than a skip signal, and loyalty's store issues a full-column
      // UPDATE writing max_redemptions, max_discount_budget_minor, budget_currency,
      // max_redemptions_per_guest, property_codes, rate_plan_codes and room_type_codes
      // from whatever the request carries.
      //
      // So a payload built only from the visible fields does not "leave the rest alone" —
      // it erases them. Renaming a capped, property-scoped campaign turned it into an
      // uncapped estate-wide discount, with nothing on screen having shown those values at
      // any point. This is a money surface; the form editing a subset must still submit
      // the whole.
      //
      // Overlaying onto the loaded promotion, rather than hydrating every field into form
      // state, is deliberate: it keeps one source of truth for fields nobody can edit, and
      // a field added to the API later is carried through automatically instead of being
      // silently dropped until someone notices.
      // Only `decisionTableRef` is still carried through unseen — BL-025 defers the
      // feature and the evaluator skips any promotion holding one, so a control here would
      // offer a setting that disables the promotion.
      ...preservedFromLoaded(data?.promotion),
      // Scope, schedule and caps are now SENT FROM THE FORM rather than copied off the
      // loaded record. They were carried through invisibly because nothing rendered them;
      // now that every one of them has a control, echoing the loaded value would override
      // whatever the operator just changed.
      scope: {
        propertyCodes: form.propertyCodes,
        roomTypeCodes: form.roomTypeCodes,
        ratePlanCodes: form.ratePlanCodes,
        channelCodes: dedupeChannelCodes(form.channelCodes)
      },
      consumableVia: form.consumableVia,
      schedule: {
        includeRanges: toApiRanges(form.stayRanges),
        bookingWindowRanges: toApiRanges(form.bookingRanges),
        blackoutRanges: toApiRanges(form.blackoutRanges),
        daysOfWeek: form.daysOfWeek,
        // Named events stay untouched: they are resolved server-side and the form has no
        // control for them.
        recurringEventRefs: data?.promotion?.schedule?.recurringEventRefs ?? [],
        advancePurchaseMinDays: toOptionalInt(form.advancePurchaseMinDays),
        advancePurchaseMaxDays: toOptionalInt(form.advancePurchaseMaxDays)
      },
      caps: {
        maxRedemptions: toOptionalIntString(form.maxRedemptions),
        maxDiscountBudgetMinor: toOptionalIntString(
          form.maxDiscountBudgetMinor
        ),
        // Sent alongside the budget so a cap and its currency cannot disagree.
        budgetCurrency: form.maxDiscountBudgetMinor.trim()
          ? form.currency.trim()
          : undefined,
        maxRedemptionsPerGuest: toOptionalInt(form.maxRedemptionsPerGuest)
      }
    }
    savePromotion({ variables: { input, publish } })
  }

  if (detailLoading)
    return (
      <>
        <PageHeader
          title={getTranslate(id ? 'Edit promotion' : 'Create promotion')}
        />
        <CircularProgress aria-label={getTranslate('Loading promotion')} />
      </>
    )
  if (detailError)
    return (
      <>
        <PageHeader title={getTranslate('Edit promotion')} />
        <Alert
          severity="error"
          action={
            <Button onClick={() => refetch()}>{getTranslate('Retry')}</Button>
          }>
          {getTranslate('Unable to load promotion.')}
        </Alert>
      </>
    )
  if (id && !data?.promotion)
    return (
      <>
        <PageHeader title={getTranslate('Edit promotion')} />
        <Alert
          severity="info"
          action={
            <Button component={LocalizedLink} to="/promotions/list">
              {getTranslate('Back to promotions')}
            </Button>
          }>
          {getTranslate('Promotion was not found.')}
        </Alert>
      </>
    )
  // Read-only is a RENDERING MODE, not a refusal. This used to return an error banner
  // instead of the record, which meant a user with read access who clicked a row in a
  // list they are permitted to read — the row's only affordance — was told they had done
  // something wrong and shown nothing. They had not asked to edit; they asked to look.
  //
  // The portal convention (docs/admin-panel/portal-ux-conventions.md, and the comment on
  // Cancel below) is that read and edit are the same screen and read-only-ness comes from
  // `canWrite` disabling the fields. The comment said that; the code did the opposite.
  const readOnly = Boolean(id && data?.promotion && !data.promotion.canWrite)

  return (
    <>
      <PageHeader
        title={getTranslate(id ? 'Edit promotion' : 'Create promotion')}
      />
      {/*
        Two different affordances for two different situations, per
        docs/admin-panel/portal-ux-conventions.md section 3.

        Editing an existing promotion is a drill-down — the record sits under the list, so it
        gets the breadcrumb the rest of the portal uses (Providers / Test Provider A). Creating
        one is a detour with nothing to breadcrumb TO yet, so it keeps the back arrow.
      */}
      {id ? (
        <Breadcrumb
          loading={detailLoading}
          levels={[
            { label: 'Promotions', link: '/promotions/list' },
            { label: data?.promotion?.name || '' }
          ]}
        />
      ) : (
        <PromotionBuilderBackButton />
      )}
      <div className={classes.header}>
        <Typography variant="h3">
          {getTranslate(id ? 'Edit promotion' : 'Create promotion')}
        </Typography>
        {/*
          Always the list, never `/promotions/${id}`. That target was a no-op the user
          could see: `/promotions/:id` and `/promotions/:id/edit` render THIS SAME
          component (promotionRoleManagement.tsx — read-only-ness comes from `canWrite`
          disabling fields, not from a different screen), so Cancel navigated from the
          builder to the builder and nothing on screen changed. Reported as "Cancel
          button in edit mode does not do anything", which was exactly right.

          Leaving is the whole meaning of Cancel here, so it must land somewhere else.
        */}
        <Button component={LocalizedLink} to="/promotions/list">
          {getTranslate('Cancel')}
        </Button>
      </div>
      {/*
        Campaign performance above the form, and only in EDIT mode. A promotion being
        created has no history, and a panel of zeroes above an empty form reads as a
        broken query rather than as a new campaign.

        Above rather than below because it is what the record IS, not what you do to it —
        an operator opening a live promotion is usually asking how it is doing before
        deciding whether to change it.
      */}
      {id && <PromotionStatsPanel promotionId={id} />}
      <Paper className={classes.paper}>
        <form onSubmit={(event) => submit(event, false)} noValidate>
          {/*
            severity="info", not "error". Nothing has gone wrong: the operator opened a
            record they are allowed to read. An error banner told them off for a click
            the list invited them to make.
          */}
          {readOnly && (
            <Alert severity="info" role="status">
              {getTranslate('You have read-only access to this promotion.')}
            </Alert>
          )}
          {/* role="alert" so a validation failure is announced. Previously pressing Save
            with an empty required field produced nothing audible: focus stayed on the
            button, the banner rendered above it and the field errors below. */}
          {formError && (
            <Alert severity="error" role="alert">
              {formError}
            </Alert>
          )}
          {/*
            The picker load failures are announced HERE, not on the fields. Two reasons,
            both checked rather than assumed:

            1. AutocompleteWithLabel renders `helpertext` as a plain sibling Typography
               with no `aria-describedby` — so field-local text reaches assistive tech
               through no channel at all. This banner is the only one that does.
            2. A field-local message is hidden the moment that field also has a
               validation error, which is exactly when the operator is looking at it.

            `role="status"` (polite), not `role="alert"`: nothing is broken and no input
            is wrong — the saved values still hold and still save. It is a standing
            condition, which is also why it is not a toast; a toast fires once and leaves
            the empty picker behind it with no explanation.

            The Retry re-runs only the query that failed, matching the `detailError`
            banner above. Without a working retry, "retry before changing it" was an
            instruction with no affordance behind it — the queries fire once on mount.
          */}
          {(providersError || channelsError) && (
            <Alert
              severity="warning"
              role="status"
              action={
                <Button
                  onClick={() => {
                    if (providersError) getProviders()
                    if (channelsError) getChannels()
                  }}>
                  {getTranslate('Retry')}
                </Button>
              }>
              {providersError && channelsError
                ? getTranslate(
                    'Could not load the provider and channel lists. Saved values are unchanged and will still save.'
                  )
                : providersError
                ? getTranslate(
                    'Could not load the provider list. The saved provider is unchanged and will still save.'
                  )
                : getTranslate(
                    'Could not load the channel list. The saved channels are unchanged and will still save.'
                  )}
            </Alert>
          )}
          {/*
            A native `fieldset[disabled]`, not `disabled` on each control. Two inputs here
            accept NO `disabled` prop at all — AutocompleteWithLabel (the provider and
            channel pickers) and TimePeriodForm (the date range) — so a per-control
            approach could not have covered the two most consequential fields.
            `fieldset[disabled]` disables every descendant form control natively,
            including any field added later, so it cannot be forgotten next time someone
            extends the form.
          */}
          <fieldset disabled={readOnly} className={classes.fieldset}>
            <SectionTitle title="Basic details" marginBottom />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  fullWidth
                  id="promotion-name"
                  label={getTranslate('Name')}
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name}
                  value={form.name}
                  onChange={(event) => update('name', event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {/*
                Single-select, unlike Channel codes: `provider_code` is REQUIRED and
                singular in the schema — one promotion is funded by exactly one
                provider. `helpertext` is lower-case in this component's props.
              */}
                <AutocompleteWithLabel
                  multiple
                  fullWidth
                  // The asterisk is appended by hand because this control cannot carry
                  // `required`. AutocompleteWithLabel omits `renderInput` (the design
                  // system owns the input) and AutocompleteProps has no `required`, so
                  // unlike the TextField this replaces, no hidden `input[required]` is
                  // rendered and assistive tech is not told the field is mandatory.
                  // Submit still enforces it (`!form.providerCode.trim()`), so the
                  // failure mode is a late error rather than a silent save — but the
                  // announcement is genuinely lost, and the test asserts the drop.
                  label={`${getTranslate('Provider code')} *`}
                  // An empty picker has two very different causes and looked identical:
                  // "this operator has no providers" and "the request failed". Apollo's
                  // default errorPolicy leaves `data` undefined and `loading` false on
                  // failure, so without this the operator sees an empty list and no signal.
                  //
                  // APPENDED to the description, never replacing it — the rule this file
                  // already states at the priority field below: an error that replaces its
                  // field's only description leaves the operator without the thing that
                  // explains what the field is for, exactly when they need it.
                  //
                  // `error` is deliberately NOT set for a load failure. It paints the field
                  // red, which is a claim about the operator's INPUT; the held value is
                  // valid and saves correctly. The alarm belongs to the form-level Alert
                  // above, which is also the only channel that reaches assistive tech —
                  // this component renders helpertext as a plain sibling Typography with no
                  // `aria-describedby` and no `aria-invalid` (verified in the installed
                  // source), so field-local text is a sighted-only trace by construction.
                  helpertext={
                    fieldErrors.providerCode ||
                    (providersError
                      ? `${getTranslate(
                          'The provider whose supply this promotion discounts.'
                        )} ${getTranslate(
                          'List unavailable — the saved code is still shown.'
                        )}`
                      : getTranslate(
                          'The provider whose supply this promotion discounts.'
                        ))
                  }
                  error={Boolean(fieldErrors.providerCode)}
                  loading={providersLoading}
                  value={selectedProvider}
                  options={providerOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionSelected={(option, selected) =>
                    option.value === selected.value
                  }
                  onChange={(_, selected) => {
                    // Take the LAST pick, not the first: choosing a second provider
                    // should replace the current one, which is what an operator
                    // correcting a mistake expects. Keeping the first would make the
                    // field feel stuck.
                    const picked = selected as ProviderOption[]
                    update(
                      'providerCode',
                      picked.length ? picked[picked.length - 1].value : ''
                    )
                  }}
                />
              </Grid>
            </Grid>
            <SectionTitle title="Discount" marginBottom />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="promotion-discount-type-label">
                    {getTranslate('Discount type')}
                  </InputLabel>
                  <Select
                    labelId="promotion-discount-type-label"
                    id="promotion-discount-type"
                    value={form.discountType}
                    onChange={(event) => {
                      // Clear the value on a type change. Percent and FixedAmount use
                      // different units (basis points vs minor units), so carrying the
                      // number across reinterprets it silently: 20 percent became 20 minor
                      // units — a 0.20 discount — and saved without complaint. The reverse
                      // direction was caught by the 0-100 check, and that asymmetry is what
                      // made the lossy direction invisible.
                      setForm((current) => ({
                        ...current,
                        discountType: event.target.value as string,
                        discountValue: ''
                      }))
                    }}>
                    <MenuItem value="Percent">
                      {getTranslate('Percent')}
                    </MenuItem>
                    <MenuItem value="FixedAmount">
                      {getTranslate('FixedAmount')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  fullWidth
                  id="promotion-discount-value"
                  label={getTranslate('Discount value')}
                  error={Boolean(fieldErrors.discountValue)}
                  // The unit, shown when there is no error to show instead. A percent field
                  // that says nothing is how the raw basis-point value read as plausible.
                  helperText={
                    fieldErrors.discountValue ||
                    (form.discountType === 'Percent'
                      ? getTranslate(
                          'Percent of the stay total, e.g. 20 or 12.5.'
                        )
                      : getTranslate(
                          'Amount in minor units, e.g. 5000 = 50.00.'
                        ))
                  }
                  value={form.discountValue}
                  onChange={(event) =>
                    update('discountValue', event.target.value)
                  }
                  inputProps={{ inputMode: 'decimal' }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl
                  required
                  fullWidth
                  error={Boolean(fieldErrors.currency)}>
                  <InputLabel id="promotion-currency-label">
                    {getTranslate('Currency')}
                  </InputLabel>
                  <Select
                    labelId="promotion-currency-label"
                    id="promotion-currency"
                    value={form.currency}
                    onChange={(event) =>
                      update('currency', event.target.value as string)
                    }>
                    {/*
                    The ISO code is appended in FA only, because in EN the label already
                    IS the code — `Label.EN` is literally 'USD'/'EUR' (types/currency.ts),
                    so appending would render "USD (USD)". The FA labels are pure Persian
                    names ('دلار'), so a Persian operator would otherwise pick a currency
                    without ever seeing the three-letter code that is what gets stored and
                    what every downstream report and rate plan shows back to them.
                  */}
                    {CurrencyList.map((currency) => (
                      <MenuItem key={currency.Value} value={currency.Value}>
                        {lang === 'fa'
                          ? `${currency.Label.FA} (${currency.Value})`
                          : currency.Label.EN}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(fieldErrors.currency) && (
                    <FormHelperText>{fieldErrors.currency}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            <ScopeSection
              propertyCodes={form.propertyCodes}
              roomTypeCodes={form.roomTypeCodes}
              ratePlanCodes={form.ratePlanCodes}
              consumableVia={form.consumableVia}
              onChange={updateList}
              propertyOptions={propertyOptions}
              roomTypeOptions={roomTypeOptions}
              ratePlanOptions={ratePlanOptions}
              propertiesLoading={accommodationsLoading}
              childrenLoading={roomTypesLoading || ratePlansLoading}
              channelField={
                <>
                  {/*
                `helpertext` is lower-case in this component's props — it is not the MUI
                `helperText` spelling, and passing that one type-errors rather than
                silently dropping the text.
              */}
                  <AutocompleteWithLabel
                    multiple
                    fullWidth
                    label={getTranslate('Channel codes')}
                    // Same reasoning as the provider field. It matters more here, because an
                    // empty channel list is LEGITIMATE ("all channels") — so a failed load is
                    // indistinguishable from the meaningful empty state, not merely from an
                    // unlikely one. Which is exactly why the description is APPENDED rather
                    // than replaced: "Leave empty for all channels" is the sentence that
                    // explains the ambiguity the failure creates, so dropping it left an
                    // empty picker with nothing stating what empty means.
                    //
                    // No `error` prop: the field would otherwise stay red through a
                    // successful save, having never been invalid.
                    helpertext={
                      channelsError
                        ? `${getTranslate(
                            'Leave empty for all channels.'
                          )} ${getTranslate('List unavailable.')}`
                        : getTranslate('Leave empty for all channels.')
                    }
                    loading={channelsLoading}
                    value={selectedChannels}
                    options={channelOptions}
                    getOptionLabel={(option) => option.label}
                    getOptionSelected={(option, selected) =>
                      option.value === selected.value
                    }
                    onChange={(_, selected) =>
                      update(
                        'channelCodes',
                        (selected as ChannelOption[])
                          .map((option) => option.value)
                          .join(', ')
                      )
                    }
                  />
                </>
              }
            />
            <ScheduleSection
              stayRanges={form.stayRanges}
              bookingRanges={form.bookingRanges}
              blackoutRanges={form.blackoutRanges}
              daysOfWeek={form.daysOfWeek}
              advancePurchaseMinDays={form.advancePurchaseMinDays}
              advancePurchaseMaxDays={form.advancePurchaseMaxDays}
              onRangesChange={updateRanges}
              onDaysChange={(days) =>
                setForm((current) => ({ ...current, daysOfWeek: days }))
              }
              onNumberChange={update}
              fieldErrors={fieldErrors}
            />
            <LimitsSection
              maxRedemptions={form.maxRedemptions}
              maxDiscountBudgetMinor={form.maxDiscountBudgetMinor}
              maxRedemptionsPerGuest={form.maxRedemptionsPerGuest}
              currency={form.currency}
              onChange={update}
              fieldErrors={fieldErrors}
            />
            <SectionTitle title="How guests get it" marginBottom />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id="promotion-code-mode-label">
                    {getTranslate('Code mode')}
                  </InputLabel>
                  <Select
                    labelId="promotion-code-mode-label"
                    id="promotion-code-mode"
                    value={form.codeMode}
                    onChange={(event) =>
                      updateCodeMode(event.target.value as string)
                    }>
                    <MenuItem value="None">{getTranslate('None')}</MenuItem>
                    <MenuItem value="SingleShared">
                      {getTranslate('SingleShared')}
                    </MenuItem>
                    <MenuItem value="BulkVoucher">
                      {getTranslate('BulkVoucher')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/*
                The SingleShared coupon. Until now the form rendered NO field for it, so a
                SingleShared promotion could be created through this screen but its code
                could only ever be set outside it — leaving a promotion that requires a code
                nobody can enter. BulkVoucher has its own path (the voucher upload screen),
                which is why only this one mode shows an input.
              */}
              {form.codeMode === 'SingleShared' && (
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    id="promotion-code"
                    inputProps={{ 'data-cy': 'PromotionCode' }}
                    label={getTranslate('Code')}
                    value={form.code}
                    onChange={(event) => update('code', event.target.value)}
                    error={Boolean(fieldErrors.code)}
                    helperText={
                      fieldErrors.code ||
                      getTranslate('The coupon guests type at checkout.')
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel id="promotion-distribution-label">
                    {getTranslate('Distribution')}
                  </InputLabel>
                  <Select
                    labelId="promotion-distribution-label"
                    id="promotion-distribution"
                    value={form.distribution}
                    onChange={(event) =>
                      updateDistribution(
                        event.target.value as PromotionDistribution
                      )
                    }>
                    <MenuItem
                      value="AutoApplied"
                      disabled={form.codeMode !== 'None'}>
                      {getTranslate('AutoApplied')}
                    </MenuItem>
                    <MenuItem value="CodeEntry">
                      {getTranslate('CodeEntry')}
                    </MenuItem>
                  </Select>
                  {form.codeMode !== 'None' && (
                    <FormHelperText>
                      {getTranslate(
                        'A promotion with a code is redeemed by entering it.'
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.advertisementOptIn}
                        disabled={form.distribution !== 'CodeEntry'}
                        onChange={(event) =>
                          update('advertisementOptIn', event.target.checked)
                        }
                      />
                    }
                    label={getTranslate('Advertise to booking partners')}
                  />
                  <FormHelperText>
                    {getTranslate(
                      'Allow this code-based promotion to appear in approved partner booking channels.'
                    )}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <SectionTitle title="Stacking" marginBottom />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.stackable}
                      onChange={(event) =>
                        update('stackable', event.target.checked)
                      }
                    />
                  }
                  label={getTranslate('Stackable')}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth error={Boolean(fieldErrors.priority)}>
                  <InputLabel id="promotion-priority-label">
                    {getTranslate('Priority')}
                  </InputLabel>
                  <Select
                    labelId="promotion-priority-label"
                    id="promotion-priority"
                    value={form.priority}
                    onChange={(event) =>
                      update('priority', String(event.target.value))
                    }>
                    {priorityOptions(form.priority).map((value) => (
                      <MenuItem key={value} value={String(value)}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  {/*
                  The error is shown ALONGSIDE the explanation, not instead of it. An
                  error that replaces its field's only description leaves the operator
                  reading "must be a whole number" with nothing left telling them what
                  the number is for — exactly when they need that most.
                */}
                  {Boolean(fieldErrors.priority) && (
                    <FormHelperText error>
                      {fieldErrors.priority}
                    </FormHelperText>
                  )}
                  <FormHelperText>
                    {getTranslate(
                      'Higher priority wins when promotions stack.'
                    )}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </fieldset>
          {/*
            Hidden, not disabled. A permanently disabled Save on a form you can never save
            is an affordance whose only content is the refusal; the banner states that
            once, at the top, where it is read.
          */}
          {!readOnly && (
            <div className={classes.actions}>
              <Button
                type="submit"
                variant="outlined"
                disabled={saving}
                data-cy="promotionSaveDraft">
                {saving ? getTranslate('Saving') : getTranslate('Save draft')}
              </Button>
              {/*
              Publish is `type="button"` and routes through the same submit(), which means it
              runs the same validation — publishing an invalid promotion is not reachable by
              taking the other door.

              This used to be inert, sitting under a notice saying publishing was unavailable.
              It was not: savePromotion has always taken a `publish` argument
              (graphql/promotions.ts:109), the BFF forwards it untouched
              (npanel-bff promotion.resolvers.go:72), and loyalty resolves `Publish: true` to
              status active (loyalty promotion.go:658-661). Only this screen was hardcoding
              false, and the notice explained a limitation that did not exist.
            */}
              <Button
                variant="contained"
                color="primary"
                disabled={saving}
                data-cy="promotionPublish"
                onClick={(event) => submit(event, true)}>
                {getTranslate('Publish')}
              </Button>
            </div>
          )}
        </form>
      </Paper>
    </>
  )
}

export default PromotionBuilder
